import React from 'react'
import InfoLine from '../../../ui/InfoLine/InfoLine'
import CopyButton from '../../../ui/CopyButton/CopyButton'
import { useSiteToast } from '../../../ui/SiteToast/SiteToastContext'
import { PREVIEW_STATE } from '../../../utils/utm/buildCampaignLinkPreview'
import {
  getDraftLinkStateLabel,
  getPersistedLinkStateLabel,
  UTM_PREVIEW_PLACEHOLDER,
} from './utmBuilderPreviewConstants'

function UtmPreviewStatus({ variant, title, children }) {
  return (
    <div className={['UtmBuilder-PreviewStatus', `UtmBuilder-PreviewStatus_${variant}`].join(' ')}>
      <p className="UtmBuilder-PreviewStatusTitle">{title}</p>
      <div className="UtmBuilder-PreviewStatusBody">{children}</div>
    </div>
  )
}

function UtmGeneratedRow({ label, value, placeholder, multiline = false }) {
  const isPlaceholder = !value && placeholder

  return (
    <div className="UtmBuilder-GeneratedRow">
      <span className="UtmBuilder-GeneratedLabel">{label}</span>
      {isPlaceholder ? (
        <p className="UtmBuilder-GeneratedPlaceholder">{placeholder}</p>
      ) : (
        <code
          className={['UtmBuilder-GeneratedValue', multiline && 'UtmBuilder-GeneratedValue_multiline']
            .filter(Boolean)
            .join(' ')}
        >
          {value}
        </code>
      )}
    </div>
  )
}

function UtmPreviewInternalContext({ isLinkActive, ownerLabel, isPersisted }) {
  const stateTitle = isPersisted ? 'Стан посилання' : 'Майбутній стан'
  const stateLabel = isPersisted
    ? getPersistedLinkStateLabel(isLinkActive)
    : getDraftLinkStateLabel(isLinkActive)

  return (
    <div className="UtmBuilder-PreviewSection">
      <h4 className="UtmBuilder-PreviewSectionTitle">Внутрішній контекст</h4>
      <InfoLine
        title={stateTitle}
        content={
          <span className={isLinkActive ? 'UtmBuilder-State_active' : 'UtmBuilder-State_inactive'}>
            {stateLabel}
          </span>
        }
      />
      <InfoLine title="Відповідальний" content={ownerLabel || UTM_PREVIEW_PLACEHOLDER.ownerEmpty} />
    </div>
  )
}

function UtmPreviewPublicFields({ preview, utmPlaceholder, shortPlaceholder }) {
  return (
    <div className="UtmBuilder-PreviewSection UtmBuilder-PreviewSection_public">
      <h4 className="UtmBuilder-PreviewSectionTitle">Публічні UTM-параметри</h4>
      <UtmGeneratedRow label="utm_source" value={preview.utm_source} placeholder={utmPlaceholder} />
      <UtmGeneratedRow label="utm_medium" value={preview.utm_medium} placeholder={utmPlaceholder} />
      <UtmGeneratedRow label="utm_campaign" value={preview.utm_campaign} placeholder={utmPlaceholder} />
      <UtmGeneratedRow label="utm_content" value={preview.utm_content} placeholder={utmPlaceholder} />
      <UtmGeneratedRow
        label="Повне UTM-посилання"
        value={preview.final_utm_url}
        placeholder={utmPlaceholder}
        multiline
      />
      <UtmGeneratedRow label="Попередній slug" value="" placeholder={shortPlaceholder} />
    </div>
  )
}

function UtmCopyableLinkRow({
  label,
  value,
  placeholder,
  multiline = false,
  copyAriaLabel,
  copyToastMessage,
  hint = null,
  showCopy = true,
}) {
  const { showSiteToast } = useSiteToast()

  if (!value && placeholder) {
    return (
      <>
        <UtmGeneratedRow label={label} value="" placeholder={placeholder} multiline={multiline} />
        {hint}
      </>
    )
  }

  const handleCopied = () => {
    showSiteToast({
      message: copyToastMessage,
      icon: 'check',
      iconWeight: 'regular',
      theme: 'default',
    })
  }

  return (
    <div className="UtmBuilder-GeneratedRow">
      <span className="UtmBuilder-GeneratedLabel">{label}</span>
      <div
        className={[
          'UtmBuilder-ShortLinkField',
          multiline ? 'UtmBuilder-ShortLinkField_multiline' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <code
          className={[
            'UtmBuilder-GeneratedValue',
            multiline ? 'UtmBuilder-GeneratedValue_multiline' : 'UtmBuilder-GeneratedValue_short',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {value}
        </code>
        {showCopy ? (
          <CopyButton
            value={value}
            ariaLabel={copyAriaLabel}
            stopPropagation={false}
            onCopied={handleCopied}
          />
        ) : null}
      </div>
      {hint}
    </div>
  )
}

function UtmShortLinkPreview({ preview, savedShortLink }) {
  const isSaved = Boolean(savedShortLink?.short_url)
  const label = isSaved ? 'Коротке посилання' : 'Попередній slug'
  const value = isSaved ? savedShortLink.short_url : preview.short_path

  return (
    <UtmCopyableLinkRow
      label={label}
      value={value}
      placeholder={UTM_PREVIEW_PLACEHOLDER.incompleteShort}
      copyAriaLabel={isSaved ? 'Скопіювати коротке посилання' : 'Скопіювати попередній slug'}
      copyToastMessage={isSaved ? 'Коротке посилання скопійовано' : 'Попередній slug скопійовано'}
      showCopy={Boolean(value)}
      hint={
        !isSaved && value ? (
          <p className="UtmBuilder-PreviewHint">Фінальне коротке посилання буде створено після збереження.</p>
        ) : null
      }
    />
  )
}

function UtmPreviewPanel({ preview, isLinkActive, savedShortLink }) {
  const ownerLabel = preview?.ownerLabel || UTM_PREVIEW_PLACEHOLDER.ownerEmpty
  const state = preview?.previewState ?? PREVIEW_STATE.EMPTY
  const isPersisted = Boolean(savedShortLink?.short_url)

  if (state === PREVIEW_STATE.EMPTY || state === PREVIEW_STATE.INCOMPLETE) {
    const missingLabels = preview?.missingFields?.map(({ label }) => label) ?? []
    const shortPlaceholder = preview?.missingFields?.some(({ key }) => key === 'title')
      ? UTM_PREVIEW_PLACEHOLDER.incompleteShortNoTitle
      : UTM_PREVIEW_PLACEHOLDER.incompleteShort

    return (
      <>
        <UtmPreviewStatus variant="info" title="Потрібно заповнити основні поля">
          <p>Щоб згенерувати UTM-посилання, заповніть обовʼязкові поля.</p>
          {missingLabels.length > 0 && (
            <p className="UtmBuilder-PreviewStatusMissing">Не заповнено: {missingLabels.join(', ')}</p>
          )}
          {preview?.warnings?.map((warning) => (
            <p key={warning} className="UtmBuilder-PreviewStatusNote">
              {warning}
            </p>
          ))}
        </UtmPreviewStatus>

        <UtmPreviewInternalContext
          isLinkActive={isLinkActive}
          ownerLabel={ownerLabel}
          isPersisted={isPersisted}
        />
        <UtmPreviewPublicFields
          preview={preview}
          utmPlaceholder={UTM_PREVIEW_PLACEHOLDER.incompleteUtm}
          shortPlaceholder={shortPlaceholder}
        />
      </>
    )
  }

  if (state === PREVIEW_STATE.INVALID) {
    const invalidMessage = preview.invalidCombinationMessage

    return (
      <>
        <UtmPreviewStatus variant="warning" title={invalidMessage?.title ?? 'Некоректна комбінація'}>
          <p>{invalidMessage?.message}</p>
          {invalidMessage?.hint && <p className="UtmBuilder-PreviewStatusHint">{invalidMessage.hint}</p>}
        </UtmPreviewStatus>

        <UtmPreviewInternalContext
          isLinkActive={isLinkActive}
          ownerLabel={ownerLabel}
          isPersisted={isPersisted}
        />
        <UtmPreviewPublicFields
          preview={preview}
          utmPlaceholder={UTM_PREVIEW_PLACEHOLDER.invalidUtm}
          shortPlaceholder={UTM_PREVIEW_PLACEHOLDER.invalidShort}
        />
      </>
    )
  }

  return (
    <>
      <UtmPreviewStatus variant="success" title="UTM готове до створення">
        <p>Усі обовʼязкові поля заповнені, комбінація каналу та формату коректна.</p>
        {preview.warnings?.map((warning) => (
          <p key={warning} className="UtmBuilder-PreviewStatusNote">
            {warning}
          </p>
        ))}
        {preview.ownerLeakDetected && (
          <p className="UtmBuilder-PreviewStatusNote UtmBuilder-PreviewStatusNote_warning">
            Увага: у публічних полях виявлено внутрішні ідентифікатори. Перевірте генерацію.
          </p>
        )}
      </UtmPreviewStatus>

      <UtmPreviewInternalContext
        isLinkActive={isLinkActive}
        ownerLabel={ownerLabel}
        isPersisted={isPersisted}
      />
      <div className="UtmBuilder-PreviewSection UtmBuilder-PreviewSection_public">
        <h4 className="UtmBuilder-PreviewSectionTitle">Публічні UTM-параметри</h4>
        <UtmGeneratedRow label="utm_source" value={preview.utm_source} />
        <UtmGeneratedRow label="utm_medium" value={preview.utm_medium} />
        <UtmGeneratedRow label="utm_campaign" value={preview.utm_campaign} />
        <UtmGeneratedRow label="utm_content" value={preview.utm_content} />
        <UtmCopyableLinkRow
          label="Повне UTM-посилання"
          value={preview.final_utm_url}
          multiline
          copyAriaLabel="Скопіювати повне UTM-посилання"
          copyToastMessage="Повне UTM-посилання скопійовано"
        />
        <UtmShortLinkPreview preview={preview} savedShortLink={savedShortLink} />
      </div>
    </>
  )
}

export default UtmPreviewPanel
